import { FC, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';
import CircularProgress from '@mui/material/CircularProgress';

import { ChargeDto } from 'src/generated/services/TFinancialApi';
import { isShipmentUnderReview } from 'src/utils/invoiceStatus';
import { useUserContext } from 'src/use-cases/user/userDetails';
import { ChipBasic } from 'src/presentations/components/atom/chip/ChipBasic';
import IconTick from 'src/assets/images/icons/tick-circle-neutral.svg';
import Thumbnail from 'src/assets/images/under-construction.svg';

import { useShipmentSelector } from '../../ShipmentSelector';
import { TableChargesItem, UCTApprovalsProps } from './types';
import { TableCharges } from './TableCharges';
import { UCTUploadDocument } from './UploadDocument';
import { ApprovalDecision } from './ApprovalDecision';

export const ApprovalsMain: FC<UCTApprovalsProps> = (props) => {
  const { invoiceSummary } = props;
  const { shipment, isLoadingShipment } = useShipmentSelector();
  const [searchParams] = useSearchParams();
  const userDetails = useUserContext();

  const dataCharges = useMemo<TableChargesItem[]>(
    () =>
      shipment?.invoiceCharges?.filter((value) => value?.accessorial && value?.approval)?.map((item: ChargeDto, index: number) => ({ no: index + 1, ...item })),
    [shipment?.invoiceCharges]
  );

  // Show loading while data is being fetched
  if (isLoadingShipment || !invoiceSummary) {
    return (
      <div className='flex items-center justify-center flex-1'>
        <CircularProgress size={40} />
      </div>
    );
  }

  let isUnderReview = isShipmentUnderReview(invoiceSummary, shipment?.shipmentInvoiceId);
  const isFromInternal = searchParams.get('ref') === 'internal';
  if (isUnderReview && userDetails?.isSystemAdmin && isFromInternal) {
    isUnderReview = false;
  }

  // If shipment is under review, show under review message
  if (isUnderReview) {
    return (
      <div className='w-full h-full p-24 flex items-center justify-center gap-12'>
        <div className='flex flex-col items-center justify-center'>
          <span>
            <img src={Thumbnail} alt='' className='w-[120px]' />
          </span>
          <h2 className='text-24 font-bold text-neutral-90 mt-32'>Under Review</h2>
          <p className='text-14 flex-regular text-neutral-70'>This shipment is under review by Freya and the support team.</p>
        </div>
      </div>
    );
  }

  return (
    <div className='flex-1 overflow-y-scroll pb-100'>
      {!invoiceSummary?.approvalRequired && (
        <div className='px-24 py-48 flex items-center min-h-[30vh] w-full border-b border-neutral-20'>
          <div className='flex flex-col justify-center items-center gap-12 w-full'>
            <img src={IconTick} alt='' className='w-48 xl:w-60' />
            <p className='text-12 xl:text-16 text-blue_grey-30'>No approvals Required</p>
          </div>
        </div>
      )}

      <UCTUploadDocument invoiceSummary={invoiceSummary} />

      {invoiceSummary?.approvalRequired && (
        <div className='pb-48'>
          {shipment.approvalTypeApprovalStatusMap?.ACCESSORIAL_CHARGE && (
            <div className='pt-30 px-24 mt-24 border-t border-neutral-20'>
              {dataCharges?.length > 0 && <TableCharges data={dataCharges} />}
              <div className='mt-30'>
                <ApprovalDecision invoiceSummary={invoiceSummary} title='Logistics Review of Accessorial Charges' approvalType='ACCESSORIAL' />
              </div>
            </div>
          )}

          {shipment.approvalTypeApprovalStatusMap?.SERVICE_TYPE && (
            <div className='pt-24 px-24 mt-24 border-t border-neutral-20'>
              <div className='relative'>
                <h3 className='mb-12 text-primary text-14 xl:text-16 font-bold'>SERVICE REQUIRING AN APPROVAL</h3>

                <div className='flex'>
                  <ChipBasic
                    variant='warning'
                    size='large'
                    label={`${invoiceSummary?.serviceTypes[0]?.modality}  - ${invoiceSummary?.serviceTypes[0]?.name}`}
                    className=''
                  />
                </div>
              </div>
              <div className='mt-30'>
                <ApprovalDecision invoiceSummary={invoiceSummary} title='Logistics Review of Service Level' approvalType='SERVICE' />
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
};
